import React, { useRef } from 'react';
import { useSelector } from 'react-redux';
import { useBaseWidget } from '../../hooks/useBaseWidget.jsx';
import { useDatasourceData } from '../../hooks/useDatasourceData.js';
import ChartContent, { DEFAULT_CHART_CONTAINER_CLASSES } from '../base/ChartContent.jsx';
import { useDatasource } from '../../hooks/useDatasource.js';
import styles from './DonutWithTargetDatasetWidget.module.scss';
import { useWidgetInnerSize } from '../../hooks/useWidgetInnerSize.js';

const TARGET_COLOR = "#d0dde2";
const TOOLTIP_COLOR = "#708090";
const TARGET_HOVER_COLOR = "#14556f";
const CHART_CONTAINER_CLASSES = DEFAULT_CHART_CONTAINER_CLASSES.concat(["chart-wrapper"]);
const ADDITIONAL_CONTENT_HEIGHT = 62;

const getOrCreateLegendList = (chart, legendContainer) => {
  let listContainer = legendContainer.querySelector("ul");

  if (!listContainer) {
    listContainer = document.createElement("ul");
    listContainer.style.margin = 0;
    listContainer.style.padding = 0;

    legendContainer.appendChild(listContainer);
  }

  return listContainer;
};

function DonutWithTargetDatasetWidget({ widgetId, widgetRef, theme }) {
    const { formatNumber, numberWithUnit, numberWithPrecision } = useBaseWidget();
    const { data: datasourceData, getCommonNumberUnit, getCommonPrecision, getNumberUnit, getNumberPrecision } = useDatasource({widgetId, live: true});
    const datasources = useSelector(state => state.dashboard.widgets[widgetId].settings?.datasources) || [];
    const dimensions = useWidgetInnerSize(widgetRef, theme);
    const { rows: filteredRows } = useDatasourceData(widgetId);
    const show_total_value = useSelector(state => state.dashboard.widgets[widgetId].settings?.show_total_value);
    const main_value_ds = useSelector(state => state.dashboard.widgets[widgetId].settings?.main_value_ds);
    const legend_text_color = useSelector(state => state.dashboard.widgets[widgetId].settings?.legend_text_color);
    const tooltipRef = useRef(null);
    const legendRef = useRef(null);

    const initTooltip = (chartData, chartRef) => {
        const tooltipEl = tooltipRef.current;
        if (tooltipEl && chartData) {
        if (tooltipEl.firstChild) {
            tooltipEl.firstChild.remove();
        }

        const { totalPercentage, valueAll, targetAll } = chartData;
        const p = document.createElement("p");
        let text = `${I18n.t("total")}: ${totalPercentage}`;
        const br = document.createElement("br");

        const numberUnit = getCommonNumberUnit();
        const numberPrecision = getCommonPrecision();
        let text2 = `${formatNumber(valueAll, null, numberPrecision)} of ${formatNumber(targetAll, numberUnit, numberPrecision)}`;
        if (show_total_value === 'false'){
            let display_datasource = datasources.find((ds) => !!main_value_ds && ds.colId === main_value_ds);
            if (display_datasource){
                const {value, target} = getValueAndTarget(display_datasource)
            if (display_datasource.name_item.length > 0){
                text = `${display_datasource.name_item}: ${((value / target) * 100).toFixed(0)}%`; 
            } else {
                text = `${((value / target) * 100).toFixed(0)}%`;
            }
            text2 = `${formatNumber(value, null, numberPrecision)} of ${formatNumber(target, display_datasource.number_unit, numberPrecision)}`;
            } else {
                text = ''
                text2 = ''
            }
        }
        const textNode = document.createTextNode(text)
        const text2Node = document.createTextNode(text2)
        p.appendChild(textNode);
        p.appendChild(br);
        p.appendChild(text2Node);

        tooltipEl.appendChild(p);
        tooltipEl.style.opacity = 1;
        tooltipEl.style.color = TOOLTIP_COLOR;
        }
    };

    // TODO: Replace with another tooltip to not have DOM manipulation
    const externalTooltipHandler = (context, {chartData, chartRef}) => {
        const tooltipEl = tooltipRef.current;
        if (tooltipEl) {
        // Tooltip Element
        const { chart, tooltip } = context;
        // not hover segment
        if (tooltip.opacity === 0) {
            initTooltip(chartData, chartRef);
            return;
        }
        // Set caret Position
        tooltipEl.classList.remove("above", "below", "no-transform");
        if (tooltip.yAlign) {
            tooltipEl.classList.add(tooltip.yAlign);
        } else {
            tooltipEl.classList.add("no-transform");
        }

        // Set Text
        if (tooltip.body) {
            if (tooltipEl.firstChild) {
                tooltipEl.firstChild.remove();
            }

            const { parsed, dataset: { data, labels }, datasetIndex } = tooltip.dataPoints[0];
            const dataLabel = labels[0];

            if (!dataLabel) {
                initTooltip(chartData, chartRef);
                return;
            }

            let dataVal = parsed;
            const dataSum = data.reduce((a, b) => a + b, 0);
            let dataPercentage;

            // Hover on grey part shows 0% when nothing is achieved.
            if (data[0] == 0 && dataVal == dataSum) {
                dataPercentage = "0%";
                dataVal = 0;
            } else {
                dataPercentage = ((dataVal / dataSum) * 100).toFixed(0) + "%";
            }

            const p = document.createElement("p");
            const text = document.createTextNode(dataLabel + ": " + dataPercentage);
            const br = document.createElement("br");
            p.appendChild(text);
            p.appendChild(br);

            const numberUnit = getNumberUnit(datasetIndex);
            const numberPrecision = getNumberPrecision(datasetIndex);
            const text2 = document.createTextNode(formatNumber(dataVal, null, numberPrecision) + " of " + formatNumber(dataSum, numberUnit, numberPrecision).toLocaleString());
            p.appendChild(text2);
            tooltipEl.appendChild(p);

            const color = tooltip.labelColors[0].backgroundColor;
            tooltipEl.style.color = color == TARGET_COLOR ? TARGET_HOVER_COLOR : color;
        }
        tooltipEl.style.opacity = 1;
        }
    };

    const getValueAndTarget = (datasource) => {
        const dataValue = filteredRows?.find(r => r?.[datasource.colId] != null)?.[datasource.colId];
        const usedValue = dataValue === void 0 ? datasource.value : dataValue;
        let value = numberWithUnit(usedValue, datasource.number_unit);
        value = isNaN(value) ? 0 : value;

        const targetValue = filteredRows?.find(r => r?.[datasource.target_colId] != null)?.[datasource.target_colId];
        const usedTarget = targetValue === void 0 ? datasource.target : targetValue;
        let target = numberWithUnit(usedTarget, datasource.number_unit);
        target = isNaN(target) ? 0 : target;

        return { value, target };
    }

    const getChartPlugins = () => {
        return [
            {
                id: "htmlLegend",
                afterUpdate: (chart, args, options) => {
                    if (!options.container?.current) return;
                    const ul = getOrCreateLegendList(chart, options.container?.current);
                    let color = options.color;
                    ul.style.color = color;

                    // Remove old legend items
                    while (ul.firstChild) {
                        ul.firstChild.remove();
                    }
                    for (let i = 0; i < chart.data.datasets.length; i++) {
                        const li = document.createElement("li");
                        const span = document.createElement("span");
                        span.style.backgroundColor = chart.data.datasets[i].backgroundColor[0];
                        li.appendChild(span);

                        if (chart.data.datasets[i].labels) {
                        const text = document.createTextNode(chart.data.datasets[i].labels[0]);
                        li.appendChild(text);
                        }

                        ul.appendChild(li);
                    }
                },
                defaults: {
                    color: '#222'
                }
            },
            {
                id: "chartArea",
                beforeDraw: function (chart, args, options) {
                    //Get ctx from string
                    let ctx = chart.ctx;
                    let fontStyle = "Roboto";
                    let color = options.color;

                    //Set font settings to draw it correctly.
                    ctx.textAlign = "center";
                    ctx.textBaseline = "middle";
                    let centerX = (chart.chartArea.left + chart.chartArea.right) / 2;
                    let centerY = (chart.chartArea.top + chart.chartArea.bottom) / 2;
                    // offset changes depends on the cutoutPercentage ie.(1-percentage)/2
                    let offset = (chart.chartArea.bottom - chart.chartArea.top) * 0.1;
                    ctx.font = 0.6 * offset + "px " + fontStyle;
                    ctx.fillStyle = color;

                    //Draw 0% text
                    ctx.fillText("0%", centerX - offset * 0.7, chart.chartArea.top + offset);
                    //Draw 100% text
                    ctx.fillText("100%", centerX - 4 * offset, centerY - 0.7 * offset);
                },
                afterUpdate: function (chart) {
                    for (let i in chart.config.data.datasets) {
                        if (!isNaN(i)) {
                        for (let j = chart.config.data.datasets[i].data.length - 1; j >= 0; --j) {
                            if (Number(j) == chart.config.data.datasets[i].data.length - 1) continue;
                            let arc = chart.getDatasetMeta(i).data[j];
                            const radiusLength = arc.outerRadius - arc.innerRadius;
                            arc.round = {
                            x: (chart.chartArea.left + chart.chartArea.right) / 2,
                            y: (chart.chartArea.top + chart.chartArea.bottom) / 2,
                            radius: arc.innerRadius + radiusLength / 2,
                            thickness: radiusLength / 2 - 1,
                            backgroundColor: arc.options.backgroundColor,
                            };
                        }
                        }
                    }
                },
                afterDraw: function (chart) {
                    let ctx = chart.ctx;
                    for (let i in chart.config.data.datasets) {
                        if (!isNaN(i)) {
                        for (let j = chart.config.data.datasets[i].data.length - 1; j >= 0; --j) {
                            if (Number(j) == chart.config.data.datasets[i].data.length - 1) continue;
                            let arc = chart.getDatasetMeta(i).data[j];
                            let endAngle = Math.PI / 2 - arc.endAngle;

                            ctx.save();
                            ctx.translate(arc.round.x, arc.round.y);
                            ctx.fillStyle = arc.round.backgroundColor;
                            ctx.beginPath();
                            ctx.arc(arc.round.radius * Math.sin(endAngle), arc.round.radius * Math.cos(endAngle), arc.round.thickness, 0, 2 * Math.PI);
                            ctx.closePath();
                            ctx.fill();
                            ctx.restore();
                        }
                        }
                    }
                },
                defaults: {
                    color: '#ccc'
                }
            },
        ];
    }

    const getChartOptions = (props) => {
        return {
        maintainAspectRatio: false,
        devicePixelRatio: 2,
        plugins: {
            legend: {
            display: false,
            },
            htmlLegend: {
            container: legendRef,
            color: legend_text_color,
            },
            chartArea: {
            color: legend_text_color,
            },
            tooltip: {
            enabled: false,
            external:  (context) => externalTooltipHandler(context, props),
            },
        },
        cutout: "60%",
        circumference: 270,
        };
    }

    const formatData = () => {
        let labels = [];
        let _datasets = [];
        let valueAll = 0;
        let targetAll = 0;
        let notConfigured = datasources.length == 0;

        datasources.forEach((d) => {
            // If any of the variables is missing dataset_id, report is not configured.
            notConfigured = notConfigured || !(d.colId || d.value) || !(d.target_colId || d.target);
            const {value, target} = getValueAndTarget(d)
            const valueLeft = Math.max(target - value, 0);

            valueAll += value;
            targetAll += target;

            labels.push(d.name_item);
            _datasets.push({
                data: [value, valueLeft],
                backgroundColor: [d.background_color, TARGET_COLOR],
                labels: [d.name_item],
            });
        });

        // Percecntage before rounding
        const totalPercentage = ((valueAll / targetAll) * 100).toFixed(0) + "%";
        const precision = getCommonPrecision();
        valueAll = numberWithPrecision(valueAll, precision);
        targetAll = numberWithPrecision(targetAll, precision);

        return {
            notConfigured,
            datasets: _datasets,
            valueAll,
            targetAll,
            totalPercentage,
        };
    };

    const renderBeforeContent = () => {
        return <div ref={legendRef} className="chart-legend" style={{ height: ADDITIONAL_CONTENT_HEIGHT }}></div>;
    }    

    return (
        <div className={styles['donut-with-target-container']}>
            {Number(dimensions?.height) > 0 && Number(dimensions?.width) > 0 && <ChartContent 
                chart_type='doughnut'
                chart_container_classes={CHART_CONTAINER_CLASSES}
                afterInitChart={({chartData, chartRef}) => initTooltip(chartData, chartRef)}
                {...{formatData, getChartOptions, getChartPlugins, data: datasourceData, widgetId, widgetRef, renderBeforeContent}} 
                tooltip={<div ref={tooltipRef} className='chart-tooltip' style={{
                    fontSize: Math.max(10, parseInt(Math.min(dimensions?.height || 0, dimensions?.width || 0) / 17))
                }} />}
                dimensions={{
                    width: Math.max(50, dimensions.width),
                    height: Math.max(50, dimensions.height - 62)
                }}
            />}
        </div>
    )
}

export default DonutWithTargetDatasetWidget;
